import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Loader2, Plus, X, Camera } from 'lucide-react';
import { isNative, pickOrCapturePhoto, hapticSuccess } from '../native';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['Boys', 'Girls', 'Mixed'];

export const CreateTeam = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    age_category: '',
    gender: '',
    coach_name: user?.name || '',
    coach_email: user?.email || '',
    players: []
  });
  const [newPlayer, setNewPlayer] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const applyLogoFile = (file) => {
    if (!file) return;
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    setLogoFile(file);
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoChange = (e) => applyLogoFile(e.target.files?.[0]);

  const handleNativeLogoCapture = async () => {
    const file = await pickOrCapturePhoto({ filename: 'team-logo.jpg' });
    if (file) applyLogoFile(file);
  };

  const addPlayer = () => {
    if (newPlayer.trim() && !formData.players.includes(newPlayer.trim())) {
      setFormData({ ...formData, players: [...formData.players, newPlayer.trim()] });
      setNewPlayer('');
    }
  };

  const removePlayer = (player) => {
    setFormData({ ...formData, players: formData.players.filter(p => p !== player) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to create a team');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create team first
      const response = await axios.post(`${API}/teams`, formData);
      const teamId = response.data.id;
      
      // Upload logo if provided
      if (logoFile) {
        setUploading(true);
        const formDataLogo = new FormData();
        formDataLogo.append('file', logoFile);
        
        try {
          await axios.post(`${API}/teams/${teamId}/upload-logo`, formDataLogo, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (logoError) {
          console.error('Failed to upload logo:', logoError);
          toast.warning('Team created but logo upload failed');
        }
      }
      
      hapticSuccess();
      toast.success('Team created successfully!');
      navigate(`/teams/${teamId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-team-page">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-2xl font-black">REGISTER TEAM</CardTitle>
              <CardDescription>Create a new team for tournaments</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Team Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Team Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Thunder Hawks"
                required
                data-testid="team-name-input"
              />
            </div>

            {/* Age & Gender Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Age Category *</Label>
                <Select 
                  value={formData.age_category} 
                  onValueChange={(v) => setFormData({ ...formData, age_category: v })}
                >
                  <SelectTrigger data-testid="age-category-select">
                    <SelectValue placeholder="Select age group" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Gender *</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(v) => setFormData({ ...formData, gender: v })}
                >
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Coach Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="coach_name">Coach Name *</Label>
                <Input
                  id="coach_name"
                  name="coach_name"
                  value={formData.coach_name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  data-testid="coach-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="coach_email">Coach Email *</Label>
                <Input
                  id="coach_email"
                  name="coach_email"
                  type="email"
                  value={formData.coach_email}
                  onChange={handleChange}
                  placeholder="coach@example.com"
                  required
                  data-testid="coach-email-input"
                />
              </div>
            </div>

            {/* Team Logo */}
            <div className="space-y-2">
              <Label htmlFor="logo">Team Logo (Optional)</Label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-primary">
                    <img 
                      src={logoPreview} 
                      alt="Team logo preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1">
                  {isNative() ? (
                    <Button type="button" variant="outline" size="sm" className="gap-2" onClick={handleNativeLogoCapture}>
                      <Camera className="w-4 h-4" />
                      {logoPreview ? 'Change Logo' : 'Add Team Logo'}
                    </Button>
                  ) : (
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="cursor-pointer"
                    />
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload team logo (JPEG, PNG, WebP, max 5MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Players */}
            <div className="space-y-2">
              <Label>Players (Roster)</Label>
              <div className="flex gap-2">
                <Input
                  value={newPlayer}
                  onChange={(e) => setNewPlayer(e.target.value)}
                  placeholder="Player name"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPlayer())}
                  data-testid="player-input"
                />
                <Button type="button" variant="outline" onClick={addPlayer} data-testid="add-player-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.players.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.players.map((player, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
                    >
                      {player}
                      <button 
                        type="button"
                        onClick={() => removePlayer(player)}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.players.length} player(s) added
              </p>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading || uploading || !formData.age_category || !formData.gender}
              data-testid="create-team-submit"
            >
              {loading || uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploading ? 'Uploading logo...' : 'Creating...'}
                </>
              ) : (
                'Register Team'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
